import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Info as InfoIcon } from "@mui/icons-material";
import { differenceInDays, format, subDays } from "date-fns";
import { apiService, API_ENDPOINTS } from "../../../config/api";

interface AdherenceData {
  date: string;
  adherence: number;
  totalParticipants: number;
  validDayCount: number;
}

interface AdherenceSummary {
  totalDays: number;
  daysWithData: number;
  averageAdherence: number;
}

interface AvgStudyAdherenceProps {
  studyId: string;
}

const getAdherenceColor = (adherence: number): string => {
  if (adherence > 90) return "#4CAF50";
  if (adherence >= 80) return "#00505C";
  if (adherence >= 70) return "#7C3AED";
  if (adherence >= 40) return "#FF9800";
  return "#F44336";
};

const formatDateLabel = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return format(date, "d MMM yy");
  } catch {
    return dateStr;
  }
};

const AvgStudyAdherence: React.FC<AvgStudyAdherenceProps> = ({ studyId }) => {
  const [data, setData] = useState<AdherenceData[]>([]);
  const [summary, setSummary] = useState<AdherenceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [showUsageHint, setShowUsageHint] = useState(false);
  const [startDate, setStartDate] = useState<string>(
    format(subDays(new Date(), 30), "yyyy-MM-dd"),
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd"),
  );
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      setDateRangeError("Please select both start and end dates");
      return false;
    }

    const diff = differenceInDays(new Date(end), new Date(start));

    if (diff < 0) {
      setDateRangeError("End date must be after start date");
      return false;
    }

    setDateRangeError(null);
    return true;
  };

  const fetchAdherenceData = async (start: string, end: string) => {
    if (!validateDateRange(start, end)) {
      setLoading(false);
      return;
    }

    if (!studyId) {
      setError("Missing studyId");
      setLoading(false);
      return;
    }

    try {
      setHasRun(true);
      setLoading(true);
      setError(null);

      const response = await apiService.get(
        `${API_ENDPOINTS.STUDY_ADHERENCE_MONITOR.DATA}?studyId=${studyId}&startDate=${start}&endDate=${end}`,
      );

      if (response.success) {
        setData(response.data || []);
        setSummary(response.summary || null);
      } else {
        setError(response.error || "Failed to load adherence data");
      }
    } catch (err: any) {
      setError(err.message || "Error fetching adherence data");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateRange = () => {
    fetchAdherenceData(startDate, endDate);
  };

  const handleResetToDefault = (days: number = 30) => {
    const defaultStart = format(subDays(new Date(), days), "yyyy-MM-dd");
    const defaultEnd = format(new Date(), "yyyy-MM-dd");
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
  };

  return (
    <Card sx={{ mt: 4, boxShadow: 2 }}>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h4"
              component="h3"
              sx={{ fontWeight: 600, color: "#8C1515", mb: 0 }}
            >
              Average Study Adherence Overview
            </Typography>
            <Button
              size="small"
              variant="text"
              sx={{ minWidth: "auto", p: "4px 8px", color: "text.secondary" }}
              onClick={() => setShowUsageHint(true)}
            >
              How to use?
            </Button>
          </Box>

          <Dialog
            open={showUsageHint}
            onClose={() => setShowUsageHint(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 600, color: "#8C1515" }}>
              How to use this graph
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" paragraph>
                  This graph helps you identify the overall average adherence
                  rate across your study. Use it to monitor any harsh days or
                  periods with compliance problems and track trends over time.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Key insights:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>
                    Track overall adherence trends across the study period
                  </li>
                  <li>Identify days with unusually low adherence rates</li>
                  <li>
                    Monitor the effectiveness of any interventions or protocol
                    changes
                  </li>
                  <li>Compare adherence across different time periods</li>
                </Typography>
              </Box>
            </DialogContent>
          </Dialog>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            gap={2}
            sx={{ alignItems: { xs: "stretch", sm: "flex-end" } }}
          >
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              inputProps={{ max: endDate }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              inputProps={{ min: startDate }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyDateRange}
            >
              Run
            </Button>
          </Stack>

          {dateRangeError && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {dateRangeError}
            </Alert>
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleResetToDefault(7)}
          >
            Last 7 Days
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleResetToDefault(15)}
          >
            Last 15 Days
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleResetToDefault(30)}
          >
            Last 30 Days
          </Button>
        </Stack>

        <Box
          sx={{
            display: "flex",
            gap: 3,
            mb: 3,
            flexWrap: "wrap",
            justifyContent: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#4CAF50",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Excellent (&gt;90%)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#00505C",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Good (80-90%)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#7C3AED",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Fair (70-80%)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#FF9800",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Poor (40-70%)</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#F44336",
                borderRadius: "2px",
              }}
            />
            <Typography variant="caption">Critical (&lt;40%)</Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : !hasRun ? (
          <Alert severity="info">
            Select a date range and click Run to load this module.
          </Alert>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : data && data.length > 0 ? (
          <Box sx={{ mb: -2 }}>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={data}
                margin={{ top: 20, right: 30, left: 60, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  angle={0}
                  textAnchor="middle"
                  height={100}
                  tick={{ fontSize: 14, fill: "#333" }}
                  interval={data.length > 30 ? Math.floor(data.length / 10) : 0}
                  tickFormatter={formatDateLabel}
                />
                <YAxis
                  label={{
                    value: "Adherence (%)",
                    angle: -90,
                    position: "insideLeft",
                  }}
                  domain={[0, 100]}
                  ticks={[0, 20, 40, 60, 80, 100]}
                  tick={{ fontSize: 14, fill: "#333" }}
                  width={50}
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload[0]) {
                      const tooltipData = payload[0].payload as AdherenceData;
                      return (
                        <Paper
                          sx={{
                            p: 1.5,
                            bgcolor: "background.paper",
                            border: "1px solid",
                            borderColor: "divider",
                          }}
                          elevation={3}
                        >
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ fontWeight: 600 }}
                          >
                            {tooltipData.date}
                          </Typography>
                          <Typography variant="caption" display="block">
                            Adherence:{" "}
                            <strong>{tooltipData.adherence.toFixed(1)}%</strong>
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {tooltipData.validDayCount} of{" "}
                            {tooltipData.totalParticipants} participants
                          </Typography>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="adherence" radius={[6, 6, 0, 0]} minPointSize={2}>
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getAdherenceColor(entry.adherence)}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {summary && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
                  gap: 2,
                  mt: 0,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Days in Range
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {summary.totalDays}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Days with Data
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {summary.daysWithData}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Average Adherence
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {summary.averageAdherence.toFixed(1)}%
                  </Typography>
                </Paper>
              </Box>
            )}
          </Box>
        ) : (
          <Alert severity="info">
            No data available for the selected date range. The chart shows all
            dates in the range, but participant data has not yet been recorded
            for this period.
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 2,
            mt: 4,
            bgcolor: "info.lighter",
            border: "1px solid",
            borderColor: "info.light",
            borderRadius: 1,
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
          }}
        >
          <InfoIcon
            sx={{
              fontSize: 20,
              color: "info.main",
              mt: 0.3,
              flexShrink: 0,
            }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              How Adherence is Calculated
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Each day's adherence percentage is calculated using the
              adherence threshold configured for this study. Participants who
              meet the study-specific threshold are counted as adherent, and
              the percentage shown is (adherent participants) / (total
              participants) × 100. To modify this threshold, go to Study
              Dashboard, open the Study dropdown, select Preferences, and edit
              the adherence threshold.
            </Typography>
          </Box>
        </Paper>
      </CardContent>
    </Card>
  );
};

export default AvgStudyAdherence;
